import simpleheat from 'simpleheat';

import {
  TPrototypePageDto,
  TPrototypePartialDto,
  TReviewUiEventDto,
} from '@app-velocity/av-api-v2';

import {
  getFullNodeForNodeId,
  loadPage,
  removeImage,
  renderImage,
} from '../../../core';

const transformData = (data: TReviewUiEventDto[]) => {
  const map: { [coord: string]: number } = {};

  data.forEach((point) => {
    const x = Math.round(point.coordinates?.x || 0);
    const y = Math.round(point.coordinates?.y || 0);

    map[`${x}|${y}`] = (map[`${x}|${y}`] || 0) + 1;
  });

  return Object.keys(map).map((key) => {
    const splited = key.split('|');

    return [Number(splited[0]), Number(splited[1]), map[key]];
  });
};

export const useHeatMap = (
  scenarioPages: TPrototypePageDto[],
  scenarioPartials: TPrototypePartialDto[]
) => {
  const clear = async () => {
    for (const page of scenarioPages) {
      await removeImage(page.fileName!);
    }

    for (const partial of scenarioPartials) {
      await removeImage(partial.fileName!);
    }
  };

  const generate = (events: TReviewUiEventDto[]) => {
    if (!events || events.length === 0) return;
    const dataPerPage: { [pageId: string]: TReviewUiEventDto[] } = {};
    const dataPerPartial: { [partialId: string]: TReviewUiEventDto[] } = {};

    events.forEach((event) => {
      if (event.pageId)
        dataPerPage[event.pageId] = [
          ...(dataPerPage[event.pageId] || []),
          event,
        ];
      else if (event.partialId) {
        dataPerPartial[event.partialId] = [
          ...(dataPerPartial[event.partialId] || []),
          event,
        ];
      }
    });

    for (const page of scenarioPages) {
      createHeatMap(dataPerPage[page.id!], page);
    }

    for (const partial of scenarioPartials) {
      createHeatMap(dataPerPartial[partial.id!], partial);
    }
  };

  const createHeatMap = async (
    data: TReviewUiEventDto[],
    pageOrPartial: TPrototypePageDto | TPrototypePartialDto
  ) => {
    if (!pageOrPartial.fileName) return;

    await loadPage();

    await removeImage(pageOrPartial.fileName);

    const fullNode = await getFullNodeForNodeId(pageOrPartial.fileName);

    if (!fullNode?.height || !fullNode.width) return;

    const { width, height } = fullNode;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const transformedData = transformData(data || []);

    const heat = simpleheat(canvas);
    heat.data(transformedData);
    heat.max(2);
    heat.radius(10, 10);
    heat.draw(0.05);

    const url = canvas.toDataURL('image/png');

    renderImage(
      pageOrPartial.fileName,
      url,
      fullNode.absoluteX,
      fullNode.absoluteY
    );
  };

  return { generate, clear };
};
